import { useSelector } from 'react-redux';
import { Txt } from '@vst/beam';
import { House, Wifi } from '@vst/beam-icons/icons';
import { useTranslation } from 'react-i18next';

import {
  AnalyticsEventNames,
  Categories,
  DataLayerEventName,
  pushToAdobeDataLayer,
} from '@app/services/adobeDataLayer';
import { StackLink } from '@app/router/constants';
import { CustomStandardModal } from '@app/components/Modal';
import { useCanChangePlan, useHasPlanChangePending, useNavigate } from '@app/hooks';
import { selectUser } from '@app/redux/auth';
import { selectDisconnectInternet } from '@app/redux/disconnect';

import { ProductInstanceStatus } from '@mfe/shared/schema-types';

import PrimaryActionCard from './PrimaryAction';
import SecondaryAction from './SecondaryAction';

import styles from './index.module.scss';

export const ManagePlanModal = ({
  isOpen,
  handleCloseModal,
  navigateToChangePlan,
}: {
  isOpen: boolean;
  handleCloseModal: () => void;
  navigateToChangePlan: () => void;
}) => {
  const { t } = useTranslation('Profile');
  const { goTo } = useNavigate();
  const canChangePlan = useCanChangePlan();
  const { success: hasDisconnected, hasCanceledDisconnectOrder, hasCIFPlan } = useSelector(selectDisconnectInternet);
  const {
    user: { productInstanceStatus },
  } = useSelector(selectUser);

  const hasCancelledInternetAccountStatus = productInstanceStatus === ProductInstanceStatus.Canceled;

  const hasDisconnectedInternet = hasDisconnected || hasCIFPlan;
  const canCancelViasatInternet =
    (!hasDisconnectedInternet || hasCanceledDisconnectOrder) && !hasCancelledInternetAccountStatus;

  const { hasPlanChangePending } = useHasPlanChangePending();

  const handleChangePlan = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    navigateToChangePlan();
  };
  const handleCancelPlan = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    pushToAdobeDataLayer({
      event: DataLayerEventName.PAGE_VIEW,
      event_name: DataLayerEventName.PAGE_VIEW,
      event_label: `${AnalyticsEventNames.CancelSubscriptionInitiated}`,
      event_category: Categories.Disconnect,
    });
    goTo(StackLink.Disconnect);
  };

  return (
    <CustomStandardModal
      size="xLarge"
      isOpen={isOpen}
      header={t('managePlan.header')}
      onClose={handleCloseModal}
      actions={{
        primary: { label: t('managePlan.close'), onClick: handleCloseModal },
      }}
    >
      <div className={styles['modal-content']}>
        <Txt variant="bodyLargeRegular">{t('managePlan.description')}</Txt>
        <div className={styles['action-cards']}>
          {canChangePlan && !hasPlanChangePending && (
            <PrimaryActionCard
              description={t('managePlan.changeActionDescription')}
              label={t('managePlan.changeActionLabel')}
              icon={Wifi}
              onClick={handleChangePlan}
            />
          )}
          <PrimaryActionCard
            description={t('managePlan.callActionDescription')}
            label={t('managePlan.callActionLabel')}
            icon={House}
            href={`tel:${t('managePlan.carePhoneNumber')}`}
          />
        </div>
        {canCancelViasatInternet && !hasPlanChangePending && (
          <SecondaryAction
            description={t('managePlan.cancelActionDescription')}
            link={t('managePlan.cancelActionLabel')}
            onClick={handleCancelPlan}
          />
        )}
        <SecondaryAction
          description={
            hasPlanChangePending
              ? t('managePlan.hasPlanChangePending.actionDescription')
              : t('managePlan.otherActionDescription')
          }
          href={`tel:${t('managePlan.carePhoneNumber')}`}
          link={t('managePlan.carePhoneNumber')}
        />
      </div>
    </CustomStandardModal>
  );
};
